import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { User, LogOut, Settings, Home, ChevronDown, ExternalLink, Mail, Bell, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Header unificado para toda la aplicación
 * - Logo a la izquierda
 * - Botón "Volver a la web" + Mensajes + Menú de usuario a la derecha
 */
export function UserHeader({ className = '' }) {
  const navigate = useNavigate();
  const { user, trabajador, logout, isAuthenticated, token } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [mensajesNoLeidos, setMensajesNoLeidos] = useState(0);
  const [campusAlertas, setCampusAlertas] = useState(0);
  const [alertasData, setAlertasData] = useState([]);
  const [showAlertas, setShowAlertas] = useState(false);
  const menuRef = useRef(null);
  const alertasRef = useRef(null);
  
  const handleLogout = () => {
    const wasExterno = user?.rol === 'externo_campus';
    logout();
    if (wasExterno) {
      // Replace fuerza navegación limpia y evita que ProtectedRoute
      // intercepte el cambio síncrono de auth y mande a /login.
      window.location.replace('/campus/acceso');
    } else {
      navigate('/');
    }
  };

  // Ir a la web principal del colegio (siempre externa, nunca history.back)
  const handleGoToWeb = () => {
    window.location.href = 'https://www.colegiotslomas.org';
  };

  const nombreCompleto = trabajador 
    ? `${trabajador.nombre} ${trabajador.apellido}` 
    : user?.email || 'Usuario';

  // isAdmin incluye tanto superadmin como admin y administracion
  const isAdmin = user?.rol === 'admin' || user?.rol === 'administracion' || user?.rol === 'superadmin';
  // Cursantes externes solo deben ver el Campus
  const isExterno = user?.rol === 'externo_campus';
  const homeHref = isExterno ? '/campus' : '/';

  // Cerrar menú al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
      if (alertasRef.current && !alertasRef.current.contains(event.target)) {
        setShowAlertas(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Obtener cantidad de mensajes no leídos
  useEffect(() => {
    let prevCount = 0;
    
    const fetchMensajesNoLeidos = async () => {
      if (!token) return;
      try {
        const res = await fetch(`${API_URL}/api/mensajes-internos/usuario/no-leidos`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const newCount = data.no_leidos || 0;
          
          // Si hay más mensajes que antes, mostrar toast
          if (newCount > prevCount && prevCount > 0) {
            toast.info('📬 Tenés un mensaje nuevo', {
              action: {
                label: 'Ver',
                onClick: () => navigate('/mensajes')
              }
            });
          }
          
          prevCount = newCount;
          setMensajesNoLeidos(newCount);
        }
      } catch (err) {
        console.error('Error fetching mensajes no leidos:', err);
      }
    };

    if (isAuthenticated) {
      fetchMensajesNoLeidos();
      // Polling cada 30 segundos
      const interval = setInterval(fetchMensajesNoLeidos, 30000);
      
      // Escuchar evento de mensajes actualizados para refresh inmediato
      const handleMensajesActualizados = () => {
        fetchMensajesNoLeidos();
      };
      window.addEventListener('mensajes-actualizados', handleMensajesActualizados);
      
      return () => {
        clearInterval(interval);
        window.removeEventListener('mensajes-actualizados', handleMensajesActualizados);
      };
    }
  }, [isAuthenticated, token, navigate]);

  // Alertas del Campus
  useEffect(() => {
    const fetchAlertasCampus = async () => {
      if (!token) return;
      try {
        const res = await fetch(`${API_URL}/api/campus/alertas/count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setCampusAlertas(data.count || 0);
        }
      } catch (err) {}
    };

    if (isAuthenticated) {
      fetchAlertasCampus();
      const interval = setInterval(fetchAlertasCampus, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, token]);

  const openAlertas = async () => {
    setShowAlertas(!showAlertas);
    if (!showAlertas) {
      try {
        const res = await fetch(`${API_URL}/api/campus/alertas`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setAlertasData(data);
        }
      } catch (err) {}
    }
  };

  const marcarTodasLeidas = async () => {
    try {
      await fetch(`${API_URL}/api/campus/alertas/leer-todas`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      setCampusAlertas(0);
      setAlertasData([]);
      setShowAlertas(false);
    } catch (err) {}
  };

  const handleAlertaClick = async (alertaId) => {
    setShowAlertas(false);
    // Marcar SOLO esta alerta como leída (no todas)
    setAlertasData(prev => prev.filter(a => a.id !== alertaId));
    setCampusAlertas(prev => Math.max(0, prev - 1));
    try {
      await fetch(`${API_URL}/api/campus/alertas/${alertaId}/leer`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {}
  };

  // No mostrar nada si no está autenticado
  if (!isAuthenticated) {
    return (
      <header className={`bg-white shadow-sm ${className}`}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo-colegio.png" alt="Logo CATSLZ" className="h-12 w-auto" />
            </Link>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline"
                onClick={handleGoToWeb}
                data-testid="header-back-web-btn"
                className="text-gray-600 hover:text-gray-800 text-sm"
              >
                <ExternalLink className="w-4 h-4 mr-1.5" />
                <span className="hidden sm:inline">Ir a la web</span>
                <span className="sm:hidden">Web</span>
              </Button>
              <Button 
                onClick={() => navigate('/login')} 
                data-testid="header-login-btn"
                className="bg-primary hover:bg-primary/90 text-white font-semibold px-6"
              >
                Iniciar Sesión
              </Button>
            </div>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className={`bg-white shadow-sm ${className}`} data-testid="unified-header">
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to={homeHref} className="flex items-center gap-3" data-testid="header-logo">
            <img src="/logo-colegio.png" alt="Logo CATSLZ" className="h-12 w-auto" />
          </Link>
          
          {/* User menu */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Botón ir a la web */}
            <Button 
              variant="outline"
              onClick={handleGoToWeb}
              data-testid="header-back-web-btn"
              className="text-gray-600 hover:text-gray-800 text-sm h-9"
            >
              <ExternalLink className="w-4 h-4 mr-1.5" />
              <span className="hidden sm:inline">Ir a la web</span>
              <span className="sm:hidden">Web</span>
            </Button>

            {/* Botón de mensajes con badge */}
            <Link
              to="/mensajes"
              className="relative flex items-center justify-center w-9 h-9 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-gray-800"
              data-testid="header-mensajes-btn"
            >
              <Mail className="w-4 h-4" />
              {mensajesNoLeidos > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                  {mensajesNoLeidos > 99 ? '99+' : mensajesNoLeidos}
                </span>
              )}
            </Link>

            {/* Campus alerts bell */}
            <div className="relative" ref={alertasRef}>
              <button
                onClick={openAlertas}
                className="relative flex items-center justify-center w-9 h-9 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-gray-800"
                data-testid="header-campus-alertas-btn"
              >
                <Bell className="w-4 h-4" />
                {campusAlertas > 0 && (
                  <span className="absolute -top-1 -right-1 bg-violet-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                    {campusAlertas > 99 ? '99+' : campusAlertas}
                  </span>
                )}
              </button>
              
              {showAlertas && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border z-50 overflow-hidden" data-testid="alertas-dropdown">
                  <div className="flex items-center justify-between px-4 py-2.5 border-b bg-gray-50">
                    <span className="text-sm font-semibold text-gray-700">Notificaciones Campus</span>
                    {alertasData.length > 0 && (
                      <button onClick={marcarTodasLeidas} className="text-xs text-violet-600 hover:text-violet-800">
                        Marcar todas como leídas
                      </button>
                    )}
                  </div>
                  <div className="max-h-[320px] overflow-y-auto">
                    {alertasData.length === 0 ? (
                      <div className="p-4 text-center text-sm text-gray-400">Sin notificaciones nuevas</div>
                    ) : (
                      alertasData.map(a => (
                        <Link
                          key={a.id}
                          to={a.link || `/campus/espacio/${a.space_id || ''}`}
                          onClick={() => handleAlertaClick(a.id)}
                          className="block px-4 py-3 hover:bg-gray-50 border-b last:border-b-0"
                          data-testid={`alerta-${a.id}`}
                        >
                          <div className="flex items-start gap-2.5">
                            <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                              <GraduationCap className="w-4 h-4 text-violet-600" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-800 truncate">{a.titulo}</p>
                              <p className="text-xs text-gray-500">{a.tipo_label} en {a.space_nombre}</p>
                              <p className="text-xs text-gray-400 mt-0.5">
                                {new Date(a.created_at).toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                          </div>
                        </Link>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Admin quick access - visible only on mobile */}
            {isAdmin && (
              <Link
                to="/admin/dashboard"
                className="sm:hidden flex items-center justify-center w-9 h-9 rounded-md border border-violet-200 text-violet-700 hover:bg-violet-50"
                data-testid="mobile-admin-btn"
              >
                <Settings className="w-4 h-4" />
              </Link>
            )}
            
            {/* User dropdown menu */}
            <div className="relative" ref={menuRef}>
              <Button 
                variant="ghost" 
                className="flex items-center gap-2 text-sm"
                onClick={() => setMenuOpen(!menuOpen)}
                data-testid="user-menu-trigger"
              >
                <User className="w-4 h-4" />
                <span className="hidden sm:inline max-w-[150px] truncate">
                  {nombreCompleto}
                </span>
                <ChevronDown className={`w-4 h-4 transition-transform ${menuOpen ? 'rotate-180' : ''}`} />
              </Button>
              
              {menuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg border border-gray-200 z-50" data-testid="user-menu-dropdown">
                  {/* User info */}
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">{nombreCompleto}</p>
                    <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                  </div>
                  
                  {/* Menu items */}
                  <div className="py-1">
                    {/* Inicio */}
                    <Link 
                      to={homeHref}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setMenuOpen(false)}
                      data-testid="menu-inicio"
                    >
                      <Home className="mr-2 h-4 w-4" />
                      {isExterno ? 'Ir al Campus' : 'Inicio'}
                    </Link>

                    {/* Mensajes (no aplica a externes) */}
                    {!isExterno && (
                      <Link 
                        to="/mensajes" 
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setMenuOpen(false)}
                        data-testid="menu-mensajes"
                      >
                        <Mail className="mr-2 h-4 w-4" />
                        Mensajes
                        {mensajesNoLeidos > 0 && (
                          <span className="ml-auto bg-red-500 text-white text-xs font-bold rounded-full px-2 py-0.5">
                            {mensajesNoLeidos}
                          </span>
                        )}
                      </Link>
                    )}
                    
                    {/* Panel de Administración (solo admin) */}
                    {isAdmin && (
                      <Link 
                        to="/admin/dashboard" 
                        className="flex items-center px-4 py-2 text-sm text-violet-700 hover:bg-violet-50 font-medium"
                        onClick={() => setMenuOpen(false)}
                        data-testid="menu-admin"
                      >
                        <Settings className="mr-2 h-4 w-4" />
                        Panel de Administración
                      </Link>
                    )}
                  </div>
                  
                  {/* Cerrar sesión */}
                  <div className="border-t border-gray-100 py-1">
                    <button
                      onClick={() => { setMenuOpen(false); handleLogout(); }}
                      className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      data-testid="menu-logout"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      Cerrar sesión
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export default UserHeader;
